"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wrench, ShieldCheck, Flame, Zap, RotateCcw, TriangleAlert as AlertTriangle, FileCheck, CircleCheck as CheckCircle2, Clock, PhoneCall, Activity, ChevronRight, Settings, Info as HelpCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
const productLines = [{
  id: "proline-gas",
  name: "ProLine Gas Water Heaters",
  badge: "Most Common Residential",
  description: "Standard atmospheric, power vent, and direct-vent gas tanks designed for reliable household hot water supply.",
  features: ["Coregard stainless steel core anode rods", "DynaClean automatic sediment-reduction system", "Intelligent control gas valves with status LEDs", "Capacities from 30 to 75 gallons"],
  commonFixes: "Pilot assembly cleaning, thermocouple replacement, gas control valve recalibration."
}, {
  id: "proline-electric",
  name: "ProLine Electric Water Heaters",
  badge: "High Durability",
  description: "Dual-element high efficiency models built with commercial-grade incoloy heating elements and Blue Diamond glass lining.",
  features: ["Dual copper or stainless incoloy elements", "Tamper-resistant brass drain valves", "Over-temperature safety cutoff (ECO trip switch)", "Uniform Energy Factor (UEF) rated configurations"],
  commonFixes: "Upper/lower thermostat replacement, element burnout renewal, reset button testing."
}, {
  id: "voltex-hybrid",
  name: "Voltex Hybrid Heat Pump",
  badge: "Up to 73% Energy Savings",
  description: "Smart heat pump hybrid units pulling ambient air heat to maximize electrical efficiency with electronic displays.",
  features: ["Four operating modes: Efficiency, Hybrid, Electric, Vacation", "Washable air intake filter system", "iCOMM smart connectivity and leak notifications", "Energy Star qualified units"],
  commonFixes: "Compressor diagnostic checks, evaporator coil cleaning, control board sensor calibration."
}, {
  id: "tankless-on-demand",
  name: "A. O. Smith Tankless (Condensing & Non-Condensing)",
  badge: "Endless Hot Water",
  description: "Continuous on-demand water heating units with primary copper and secondary stainless steel heat exchangers.",
  features: ["Ultra-low NOx gas combustion system", "Integrated digital control with exact error readout", "Recirculation pump compatibility", "Space-saving wall-mount layout"],
  commonFixes: "Mineral descaling flush, flame sensor polishing, venting pressure switch inspection."
}];
const troubleShootingItems = [{
  id: "item-1",
  code: "Status Light: 4 Flashes (Gas Valve)",
  title: "High Temperature Limit (ECO Open)",
  symptom: "Water is lukewarm or stone cold, and the status LED blinks 4 times repeatedly.",
  cause: "The emergency cut-off safety switch tripped because tank water exceeded maximum safe operating temperature, or the internal thermal fuse failed.",
  solution: "Turn gas control dial to OFF. Check if temperature setting was too high. If the fault persists upon restart, the gas control thermopile or electronic valve module requires testing by our technician."
}, {
  id: "item-2",
  code: "Error Code: E02 / E03 (Tankless)",
  title: "Ignition Failure or Flame Rod Detection Loss",
  symptom: "Tankless display beeps and shows error E02 or E03 during shower or hot tap use.",
  cause: "Inadequate gas supply pressure, dirty flame rod sensor, or blocked air intake/exhaust venting.",
  solution: "Confirm the external gas shutoff valve is open. Clean any debris blocking external exhaust terminals. If gas supply is normal, our certified technician will clean the flame ionization sensor and adjust gas manifold pressure."
}, {
  id: "item-3",
  code: "Status Light: 5 Flashes (Gas Control)",
  title: "Sensor Failure / Thermistor Fault",
  symptom: "Gas burner will not fire up, status light blinks 5 times consecutively.",
  cause: "The temperature sensing thermistor wire inside the tank has shorted out or lost communication with the main control module.",
  solution: "Requires professional multi-meter ohm testing. The gas control valve will typically need factory-grade OEM replacement under warranty."
}, {
  id: "item-4",
  code: "Electric Models: Tripped Red Reset Button",
  title: "Upper Thermostat Thermal Cutoff",
  symptom: "No hot water at all in electric unit; the red button on the upper thermostat has popped outward.",
  cause: "Grounded or shorted heating element, faulty lower thermostat continuing to heat without limit, or loose electrical lug wiring.",
  solution: "Shut down breaker. Do not simply press the button repeatedly without testing. Our team tests both upper/lower elements with a continuity meter to resolve the electrical short safely."
}, {
  id: "item-5",
  code: "Voltex Models: Code ECC / E01",
  title: "Compressor Thermal Overload or Air Filter Restricted",
  symptom: "Hybrid unit reverts to electric-only mode, showing filter or compressor service alert.",
  cause: "Dust and lint accumulation on the top intake air filter restricting airflow to the heat pump evaporator coil.",
  solution: "Slide out the top washable air filter, rinse thoroughly under running water, dry completely, and reinstall. If error persists, call us for a refrigerant pressure inspection."
}];
const warrantyChecklist = [{
  title: "Locate Your Serial Number",
  detail: "Located on the rating plate decal on the lower front side of your A. O. Smith tank."
}, {
  title: "Decipher Tank Age & Coverage",
  detail: "The first 4 digits signify year and week of manufacture (e.g., 2145 = 45th week of 2021)."
}, {
  title: "Standard 6-Year & 10-Year Protection",
  detail: "Covers inner tank leaks and factory parts including gas valves, burner assemblies, and elements."
}, {
  title: "Authorized Jonesboro Warranty Support",
  detail: "We handle OEM warranty claims and supply original A. O. Smith certified replacement components."
}];
export default function AosmithServices() {
  const shouldReduceMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState("proline-gas");
  return <section id="aosmithwaterheaterservices" data-nav="light" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider mb-3">
            <Wrench className="size-3.5 text-accent" />
            <span>Certified Brand Expertise</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight mb-4">
            A. O. Smith Water Heater Services in Jonesboro
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            From high-efficiency Voltex hybrid heat pumps to heavy-duty ProLine gas and electric tanks, we provide complete troubleshooting, rapid OEM repairs, and warranty-compliant replacements.
          </p>
        </div>

        {/* Hero Alert & Emergency Notice */}
        <div className="mb-12">
          <Alert className="border-accent/40 bg-card shadow-sm">
            <Activity className="size-4 text-accent" />
            <AlertTitle className="text-foreground font-semibold flex items-center justify-between flex-wrap gap-2">
              <span>Fast Emergency Diagnostic & OEM Parts In-Stock</span>
              <span className="text-xs font-normal text-muted-foreground flex items-center gap-1">
                <Clock className="size-3 text-accent" /> Available 24/7 across Jonesboro & Clayton County
              </span>
            </AlertTitle>
            <AlertDescription className="text-muted-foreground">
              Experiencing an A. O. Smith flashing status code, cold water emergency, or bottom tank leakage? Our licensed plumbers carry authentic A. O. Smith thermocouples, gas valves, and incoloy heating elements on service trucks.
            </AlertDescription>
          </Alert>
        </div>

        {/* Main Content: Product Lines Grid / Tabs */}
        <div className="mb-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
            <div>
              <h3 className="text-2xl font-bold font-default text-foreground">
                A. O. Smith Product Lines Serviced
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Select a model category to explore technical specifications and common repair procedures.
              </p>
            </div>
            <Link to="/water-heater-repair/">
              <Button variant="outline" size="sm" className="w-fit">
                View General Water Heater Services
                <ChevronRight className="size-4 ml-1" />
              </Button>
            </Link>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-6">
            <TabsList className="w-full justify-start overflow-x-auto h-auto p-1 bg-muted rounded-xl flex-wrap">
              <TabsTrigger value="proline-gas" className="py-2.5 px-4 text-xs sm:text-sm">
                <Flame className="size-4 mr-2 text-accent" />
                ProLine Gas Tanks
              </TabsTrigger>
              <TabsTrigger value="proline-electric" className="py-2.5 px-4 text-xs sm:text-sm">
                <Zap className="size-4 mr-2 text-accent" />
                ProLine Electric
              </TabsTrigger>
              <TabsTrigger value="voltex-hybrid" className="py-2.5 px-4 text-xs sm:text-sm">
                <RotateCcw className="size-4 mr-2 text-accent" />
                Voltex Hybrid Heat Pump
              </TabsTrigger>
              <TabsTrigger value="tankless-on-demand" className="py-2.5 px-4 text-xs sm:text-sm">
                <Settings className="size-4 mr-2 text-accent" />
                Tankless Units
              </TabsTrigger>
            </TabsList>

            {productLines.map((item, i) => <TabsContent key={item.id} value={item.id} className="mt-0">
                <motion.div data-index={i} initial={shouldReduceMotion ? {
              opacity: 1
            } : {
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3
            }}>
                  <Card className="border border-border bg-card shadow-sm overflow-hidden">
                    <CardHeader className="bg-secondary/40 border-b border-border pb-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div>
                          <Badge variant="outline" className="mb-2 bg-background">
                            {item.badge}
                          </Badge>
                          <CardTitle className="text-xl sm:text-2xl font-bold font-default text-foreground">
                            {item.name}
                          </CardTitle>
                        </div>
                        <a href="tel:4708479076">
                          <Button size="sm" className="w-full sm:w-auto">
                            <PhoneCall className="size-3.5 mr-1" />
                            Request Service
                          </Button>
                        </a>
                      </div>
                      <CardDescription className="text-sm mt-2 text-muted-foreground">
                        {item.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="text-sm font-semibold uppercase tracking-wider text-foreground mb-3 flex items-center gap-1.5">
                            <CheckCircle2 className="size-4 text-accent" />
                            Engineered Features & Build
                          </h4>
                          <ul className="space-y-2">
                            {item.features.map((feat, fIdx) => <li key={fIdx} data-index={fIdx} className="text-sm text-muted-foreground flex items-start gap-2">
                                <span className="size-1.5 rounded-full bg-accent mt-2 shrink-0" />
                                <span>{feat}</span>
                              </li>)}
                          </ul>
                        </div>
                        <div className="bg-background rounded-lg p-4 border border-border flex flex-col justify-between">
                          <div>
                            <h4 className="text-sm font-semibold uppercase tracking-wider text-foreground mb-2 flex items-center gap-1.5">
                              <Wrench className="size-4 text-accent" />
                              Common Specialist Repairs
                            </h4>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                              {item.commonFixes}
                            </p>
                          </div>
                          <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                            <span>OEM Certified Components</span>
                            <span className="font-semibold text-foreground">Same-Day Availability</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              </TabsContent>)}
          </Tabs>
        </div>

        {/* Brand Troubleshooting & Error Codes Accordion */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 items-start">
          <div className="lg:col-span-5 space-y-6">
            <div>
              <Badge variant="secondary" className="mb-2">
                Troubleshooting Guide
              </Badge>
              <h3 className="text-2xl sm:text-3xl font-bold font-default text-foreground">
                A. O. Smith Error Codes & Diagnostics
              </h3>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                Modern A. O. Smith water heaters communicate status through flashing LED lights on the gas control thermostat or digital error codes on LCD screens. Use this guide to identify the source before scheduling a visit.
              </p>
            </div>

            <Card className="border border-border bg-card">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="p-2.5 rounded-lg bg-destructive/10 text-destructive shrink-0 mt-0.5">
                    <AlertTriangle className="size-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-foreground">
                      Gas Smell or Major Water Leak?
                    </h4>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      If you smell natural gas, shut off the gas valve immediately, leave the home, and call the gas utility. For active tank flooding, turn the top cold water shutoff valve clockwise.
                    </p>
                  </div>
                </div>
                <Separator />
                <div className="flex items-center justify-between gap-4">
                  <div className="text-xs text-muted-foreground">
                    Need on-site diagnosis?
                  </div>
                  <Link to="/contact/">
                    <Button variant="outline" size="sm">
                      Book Technician
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            <div className="rounded-xl bg-secondary/30 border border-border p-5">
              <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
                <HelpCircle className="size-4 text-accent" />
                Reading the LED Status Light
              </h4>
              <p className="text-xs text-muted-foreground leading-relaxed">
                On Honeywell and White-Rodgers gas valves used on A. O. Smith heaters, 1 flash every 3 seconds indicates normal operation. 2 to 7 flashes indicate lockouts, low thermopile voltage, or sensor errors.
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <Card className="border border-border bg-card shadow-sm">
              <CardHeader className="pb-2 border-b border-border">
                <CardTitle className="text-lg font-bold font-default text-foreground">
                  Frequent Error Codes & Direct Solutions
                </CardTitle>
                <CardDescription className="text-xs text-muted-foreground">
                  Click each code to review symptom breakdown, root causes, and corrective action.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 sm:p-6">
                <Accordion type="single" collapsible className="w-full">
                  {troubleShootingItems.map((item, i) => <AccordionItem key={item.id} value={item.id} data-index={i} className="border-border">
                      <AccordionTrigger className="hover:no-underline py-3 px-2 rounded-md transition-colors hover:bg-secondary/40 text-left">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                          <span className="font-mono text-xs px-2 py-0.5 rounded bg-muted text-foreground font-semibold">
                            {item.code}
                          </span>
                          <span className="text-sm font-semibold text-foreground">
                            {item.title}
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-2 pt-2 pb-4 text-xs sm:text-sm text-muted-foreground space-y-3">
                        <div>
                          <strong className="text-foreground">Symptom: </strong>
                          {item.symptom}
                        </div>
                        <div>
                          <strong className="text-foreground">Underlying Cause: </strong>
                          {item.cause}
                        </div>
                        <div className="bg-background border border-border p-3 rounded-lg">
                          <strong className="text-accent font-semibold">Recommended Fix: </strong>
                          {item.solution}
                        </div>
                      </AccordionContent>
                    </AccordionItem>)}
                </Accordion>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Warranty Support & Registration Assistance */}
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-10 shadow-sm relative overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-4">
              <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-accent bg-accent/10 px-2.5 py-1 rounded-md">
                <ShieldCheck className="size-4" />
                Factory Warranty Guidance
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold font-default text-foreground">
                A. O. Smith Warranty Claims & Verification
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Most A. O. Smith residential tanks come with a 6-year, 10-year, or lifetime limited tank warranty. We check your serial number against official factory databases, submit parts claims directly, and ensure you do not pay out-of-pocket for covered tank shells or OEM modules.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                {warrantyChecklist.map((w, wIdx) => <div key={wIdx} data-index={wIdx} className="flex items-start gap-2.5 p-3 rounded-lg bg-background border border-border">
                    <FileCheck className="size-4 text-accent shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-xs font-bold text-foreground">
                        {w.title}
                      </h4>
                      <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                        {w.detail}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>

            <div className="lg:col-span-5 bg-secondary/50 rounded-xl p-6 border border-border flex flex-col justify-between space-y-6">
              <div>
                <h4 className="text-lg font-bold font-default text-foreground mb-2">
                  Need Help With Your A. O. Smith Unit?
                </h4>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Call our local Jonesboro emergency crew. We arrive with diagnosis instruments, genuine factory replacement parts, and upfront replacement estimates if your tank has leaked.
                </p>
              </div>

              <div className="space-y-3">
                <a href="tel:4708479076" className="w-full block">
                  <Button size="lg" className="w-full gap-2 font-semibold">
                    <PhoneCall className="size-4" />
                    Call (470) 847-9076
                  </Button>
                </a>
                <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                  <Clock className="size-3.5 text-accent" />
                  <span>Prompt Local Response in Jonesboro, GA</span>
                </div>
              </div>

              <div className="pt-3 border-t border-border flex flex-wrap items-center justify-between gap-2 text-xs">
                <Link to="/water-heater-replacement/" className="text-foreground font-medium hover:underline">
                  Replacement Options &rarr;
                </Link>
                <Link to="/brands/" className="text-foreground font-medium hover:underline">
                  All Supported Brands &rarr;
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}